import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import {
    Message,
    MessageAttachment,
    MessageComment,
    MessageViewer,
} from 'src/domain/message';
import { UserRole } from 'src/domain/user';
import { PaginatedResponse } from 'src/domain/pagination';
import { SASInfo } from 'src/domain/user';
import container from 'src/container';
import { useNotification } from 'src/components/Notification/useNotfication';
import { Label as InputLabel } from 'src/components/FormComponents/Input';
import ConfimationModalProvider, {
    useConfirmationModalContext,
} from 'src/components/Modals/ConfirmationModal/ConfirmationModalProvider';
import {
    CaretIcon,
    EyeIcon,
    MessageBubbleIcon,
    PenIcon,
    TrashIcon,
} from 'src/components/Icons/icons';
import { MessageAttachmentsField } from './MessageAttachmentsField';
import {
    MessageAttachmentConversionError,
    uploadMessageAttachments,
} from './messageAttachmentUpload';

import { MessageAttachmentPreviewModal } from './MessageAttachmentPreviewModal';
import { MessageCommentItem } from './MessageCommentItem';
import { LinkifiedText } from './LinkifiedText';
import {
    COMMENT_PREVIEW_LIMIT,
    loadMessageComments,
} from './loadMessageComments';

const MESSAGE_COMMENTS_REFRESH_INTERVAL_MS = 30 * 1000;

const getVisibleRefreshInterval = () => {
    if (typeof document === 'undefined') {
        return MESSAGE_COMMENTS_REFRESH_INTERVAL_MS;
    }

    return document.visibilityState === 'visible'
        ? MESSAGE_COMMENTS_REFRESH_INTERVAL_MS
        : 0;
};

export type MessageAttachmentView = MessageAttachment & {
    url: string;
    thumbnailUrl?: string;
};

export type MessageItemProps = {
    message: Message;
    createdAt: string;
    sentByLabel?: string;
    groupLabels?: string[];
    parentGroupLabelsByConsumerId?: Map<string, string>;
    recipientsById?: Map<string, string>;
    senderBadge?: {
        label: string;
        className: string;
    };
    attachments?: MessageAttachmentView[];
    canDelete: boolean;
    canEdit: boolean;
    currentUserId?: string;
    isDeleting: boolean;
    isTogglingComments: boolean;
    onDelete: () => void;
    onUpdated?: () => Promise<void> | void;
    targetLabelKey?: string;
    canComment?: boolean;
    canCloseComments?: boolean;
    onCloseComments?: () => Promise<void> | void;
    onVisibleRead?: (message: Message) => Promise<void> | void;
};

type PreviewAttachmentState = {
    attachment: MessageAttachmentView;
    imageIndex?: number;
};

const resolveAttachmentType = (mimeType?: string) => {
    if (!mimeType) return 'file';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType === 'application/pdf') return 'pdf';
    return 'file';
};

export const MessageItem = ({
    message,
    createdAt,
    groupLabels,
    parentGroupLabelsByConsumerId,
    recipientsById,
    senderBadge,
    attachments,
    canDelete,
    canEdit,
    currentUserId,
    isDeleting,
    isTogglingComments,
    onDelete,
    onUpdated,
    targetLabelKey = 'modules.messages.target',
    canComment,
    canCloseComments,
    onCloseComments,
    onVisibleRead,
}: MessageItemProps) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const [previewAttachment, setPreviewAttachment] =
        useState<PreviewAttachmentState | null>(null);
    const [commentBody, setCommentBody] = useState('');
    const [isCommentComposerOpen, setIsCommentComposerOpen] = useState(false);
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState<string | null>(
        null,
    );
    const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
        null,
    );
    const [isCommentsExpanded, setIsCommentsExpanded] = useState(false);
    const [isViewersDropdownOpen, setIsViewersDropdownOpen] = useState(false);
    const [hasRequestedCommentViewers, setHasRequestedCommentViewers] =
        useState(false);
    const articleRef = useRef<HTMLElement | null>(null);
    const messageRef = useRef(message);

    const {
        cradle: { munduClient, storageService },
    } = container;
    const [isActionsOpen, setIsActionsOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [editSubject, setEditSubject] = useState(message.subject);
    const [editBody, setEditBody] = useState(message.body);
    const [existingAttachments, setExistingAttachments] = useState<
        MessageAttachment[]
    >(message.attachments ?? []);
    const [newAttachments, setNewAttachments] = useState<File[]>([]);
    const actionsRef = useRef<HTMLDivElement | null>(null);

    const recipientLabels = useMemo(() => {
        if (message.target.sendToAll) return undefined;
        if (groupLabels?.length) return undefined;
        if (message.senderRole === UserRole.nextOfKin && message.receiverId) {
            const parentGroupLabel = parentGroupLabelsByConsumerId?.get(
                message.receiverId,
            );
            if (parentGroupLabel) {
                return parentGroupLabel;
            }
        }
        if (!message.recipientIds.length) return undefined;

        const resolved = recipientsById
            ? message.recipientIds.map(id => recipientsById.get(id) ?? id)
            : message.recipientIds;

        return resolved.join(', ');
    }, [
        groupLabels,
        message.recipientIds,
        message.receiverId,
        message.senderRole,
        message.target.sendToAll,
        parentGroupLabelsByConsumerId,
        recipientsById,
    ]);

    const labelKey = groupLabels ? targetLabelKey : 'modules.messages.target';
    const targetSummary = message.target.sendToAll
        ? t('modules.messages.targetAll')
        : groupLabels?.length
        ? t(labelKey, {
              value: groupLabels.join(', '),
          })
        : recipientLabels
        ? t(labelKey, {
              value: recipientLabels,
          })
        : undefined;

    const {
        data: commentsResponse,
        mutate: mutateComments,
        isValidating,
    } = useSWR<PaginatedResponse<MessageComment> | MessageComment[]>(
        ['messageComments', message.id, isCommentsExpanded ? 'all' : 'preview'],
        async () =>
            loadMessageComments(
                munduClient.listMessageComments,
                message.id,
                isCommentsExpanded,
            ),
        {
            revalidateOnFocus: true,
            refreshWhenHidden: false,
            refreshInterval: getVisibleRefreshInterval,
        },
    );

    const comments = useMemo(() => {
        const data = Array.isArray(commentsResponse)
            ? commentsResponse
            : commentsResponse?.data ?? [];
        return [...data].sort(
            (left, right) =>
                Date.parse(left.createdDate) - Date.parse(right.createdDate),
        );
    }, [commentsResponse]);
    const commentsTotal = Array.isArray(commentsResponse)
        ? commentsResponse.length
        : commentsResponse?.info?.total ?? comments.length;
    const commentsEnabled = Boolean(message.commentsEnabled);
    const hasCollapsibleComments = commentsTotal > COMMENT_PREVIEW_LIMIT;
    const visibleComments =
        hasCollapsibleComments && !isCommentsExpanded
            ? comments.slice(-COMMENT_PREVIEW_LIMIT)
            : comments;

    const hasActions = canCloseComments || canEdit || canDelete;
    const viewCount = message.viewCount ?? 0;
    const imageAttachments = useMemo(
        () =>
            (attachments ?? []).filter(
                attachment =>
                    resolveAttachmentType(attachment.mimeType) === 'image',
            ),
        [attachments],
    );

    const { data: viewersResponse, isValidating: isLoadingViewers } = useSWR<
        MessageViewer[]
    >(
        (isViewersDropdownOpen || hasRequestedCommentViewers) && viewCount > 0
            ? ['messageViewers', message.id]
            : null,
        async () => munduClient.listMessageViewers(message.id),
        { revalidateOnFocus: false },
    );

    const viewerLabels = useMemo(
        () =>
            (viewersResponse ?? []).map(viewer => {
                const mapped = recipientsById?.get(viewer.userId);

                return {
                    ...viewer,
                    label:
                        mapped ??
                        viewer.viewerName ??
                        t('modules.messages.viewsUnknownViewer'),
                };
            }),
        [recipientsById, t, viewersResponse],
    );

    useEffect(() => {
        if (!isActionsOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (
                actionsRef.current &&
                !actionsRef.current.contains(event.target as Node)
            ) {
                setIsActionsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isActionsOpen]);

    useEffect(() => {
        messageRef.current = message;
    }, [message]);

    useEffect(() => {
        if (!message.isUnreadByMe || !onVisibleRead) {
            return;
        }

        const target = articleRef.current;
        if (!target || typeof IntersectionObserver === 'undefined') {
            return;
        }

        let timer: ReturnType<typeof setTimeout> | undefined;
        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0];

                if (entry?.isIntersecting && entry.intersectionRatio >= 0.6) {
                    timer = setTimeout(() => {
                        void onVisibleRead(messageRef.current);
                    }, 700);
                } else if (timer) {
                    clearTimeout(timer);
                    timer = undefined;
                }
            },
            {
                threshold: [0, 0.6, 1],
            },
        );

        observer.observe(target);

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            observer.disconnect();
        };
    }, [
        message.id,
        message.isUnreadByMe,
        message.unreadActivityAt,
        onVisibleRead,
    ]);

    const handleSubmitComment = async () => {
        if (!canComment || !commentsEnabled || isSubmittingComment) {
            return;
        }

        const trimmed = commentBody.trim();
        if (!trimmed) return;

        setIsSubmittingComment(true);

        try {
            await munduClient.createMessageComment(message.id, {
                body: trimmed,
            });
            setCommentBody('');
            setIsCommentComposerOpen(false);
            await mutateComments();
        } catch (error) {
            if (error instanceof Error) {
                notify(error.message, true);
            } else {
                notify('Failed to send comment', true);
            }
        } finally {
            setIsSubmittingComment(false);
        }
    };

    const handleCloseComments = async () => {
        if (!canCloseComments || !onCloseComments) return;
        setIsActionsOpen(false);
        await onCloseComments();
    };

    const handleUpdateComment = async (commentId: string, body: string) => {
        try {
            setEditingCommentId(commentId);
            await munduClient.updateMessageComment(message.id, commentId, {
                body,
            });
            await mutateComments();
            notify(t('modules.messages.commentsEditSuccess'));
        } catch (error) {
            if (error instanceof Error) {
                notify(error.message, true);
            } else {
                notify('Failed to update comment', true);
            }
        } finally {
            setEditingCommentId(null);
        }
    };

    const handleDeleteComment = async (commentId: string) => {
        try {
            setDeletingCommentId(commentId);
            await munduClient.deleteMessageComment(message.id, commentId);
            await mutateComments();
            notify(t('modules.messages.commentsDeleteSuccess'));
        } catch (error) {
            if (error instanceof Error) {
                notify(error.message, true);
            } else {
                notify('Failed to delete comment', true);
            }
        } finally {
            setDeletingCommentId(null);
        }
    };

    const handleOpenEditModal = () => {
        setEditSubject(message.subject);
        setEditBody(message.body);
        setExistingAttachments(message.attachments ?? []);
        setNewAttachments([]);
        setIsEditModalOpen(true);
        setIsActionsOpen(false);
    };

    const handleSaveEdit = async () => {
        if (isSavingEdit) {
            return;
        }

        const subject = editSubject.trim();
        if (!subject.length) {
            notify(t('validation.requiredField'), true);
            return;
        }

        setIsSavingEdit(true);

        try {
            const nextAttachments: MessageAttachment[] = [
                ...existingAttachments,
            ];

            let attachmentsContainerName =
                message.attachmentsContainerName || undefined;
            let sas: SASInfo | undefined;

            if (newAttachments.length) {
                sas = await munduClient.getMessageMediaSas();

                if (
                    attachmentsContainerName &&
                    attachmentsContainerName !== sas.containerName
                ) {
                    throw new Error('Invalid attachments container');
                }

                attachmentsContainerName = sas.containerName;

                nextAttachments.push(
                    ...(await uploadMessageAttachments(
                        newAttachments,
                        sas,
                        storageService,
                    )),
                );
            }

            await munduClient.updateMessage(message.id, {
                subject,
                body: editBody.trim(),
                attachments: nextAttachments.length ? nextAttachments : [],
                attachmentsContainerName: nextAttachments.length
                    ? attachmentsContainerName
                    : undefined,
            });

            if (onUpdated) {
                await onUpdated();
            }

            notify(t('modules.messages.editSuccess'));
            setIsEditModalOpen(false);
        } catch (error) {
            if (error instanceof MessageAttachmentConversionError) {
                notify(
                    t('modules.messages.form.attachmentConversionFailed'),
                    true,
                );
            } else if (error instanceof Error) {
                notify(error.message, true);
            } else {
                notify('Failed to update message', true);
            }
        } finally {
            setIsSavingEdit(false);
        }
    };

    const isFromRelative = message.senderRole === UserRole.nextOfKin;
    const hasContentBelowHeader =
        Boolean(attachments?.length) || commentsEnabled || comments.length > 0;

    return (
        <article
            ref={articleRef}
            className={`min-w-0 max-w-full rounded-2xl px-3 py-3 shadow-sm bg-white transition-colors sm:px-4 ${
                message.isUnreadByMe
                    ? 'border-2 border-[#FF7979]'
                    : 'border border-starkWhite'
            }`}
        >
            {previewAttachment && (
                <MessageAttachmentPreviewModal
                    attachment={previewAttachment.attachment}
                    attachments={
                        previewAttachment.imageIndex !== undefined
                            ? imageAttachments
                            : undefined
                    }
                    initialIndex={previewAttachment.imageIndex}
                    onClose={() => setPreviewAttachment(null)}
                />
            )}
            <div
                className={
                    isFromRelative
                        ? `-mx-3 -mt-3 px-3 pt-3 pb-3 bg-green-100 sm:-mx-4 sm:px-4 ${
                              hasContentBelowHeader
                                  ? 'rounded-t-2xl'
                                  : '-mb-3 rounded-2xl'
                          }`
                        : ''
                }
            >
                <div className="flex min-w-0 gap-3 flex-row items-start justify-between">
                    <div className="min-w-0">
                        <p className="font-semibold text-lg leading-tight text-woodyBrown break-words [overflow-wrap:anywhere]">
                            {message.subject}
                        </p>
                        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-woodyBrown/55 break-words [overflow-wrap:anywhere]">
                            <span className="font-medium text-woodyBrown break-words [overflow-wrap:anywhere]">
                                {message.senderName ||
                                    t('modules.messages.sentByUnknown')}
                            </span>
                            {senderBadge && (
                                <span
                                    className={`inline-flex items-center rounded-full px-2 py-px text-[10px] font-semibold uppercase tracking-wide ${senderBadge.className}`}
                                >
                                    {senderBadge.label}
                                </span>
                            )}
                            <span aria-hidden="true">·</span>
                            <span>{createdAt}</span>
                            {targetSummary && (
                                <>
                                    <span aria-hidden="true">·</span>
                                    <span className="break-words [overflow-wrap:anywhere]">
                                        {targetSummary}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <div
                        className="flex flex-shrink-0 items-center gap-1.5"
                        ref={actionsRef}
                    >
                        {hasActions && (
                            <div className="relative">
                                <button
                                    type="button"
                                    className="h-8 w-8 rounded-full border border-woodyBrown/25 bg-white text-woodyBrown hover:bg-woodyBrown/10"
                                    onClick={() =>
                                        setIsActionsOpen(current => !current)
                                    }
                                    aria-label={t('modules.messages.actions')}
                                >
                                    ⋯
                                </button>

                                <ConfimationModalProvider
                                    title={t(
                                        'modules.messages.deleteConfirmTitle',
                                    )}
                                    message={t(
                                        'modules.messages.deleteConfirmMessage',
                                    )}
                                >
                                    {isActionsOpen && (
                                        <div className="absolute right-0 mt-2 min-w-[180px] rounded-lg border border-starkWhite bg-white shadow-lg z-20 py-1">
                                            {canCloseComments && (
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-woodyBrown hover:bg-woodyBrown/5 disabled:opacity-60"
                                                    onClick={
                                                        handleCloseComments
                                                    }
                                                    disabled={
                                                        isTogglingComments
                                                    }
                                                >
                                                    <span className="relative h-4 w-4">
                                                        <MessageBubbleIcon />
                                                    </span>
                                                    {t(
                                                        'modules.messages.commentsClose',
                                                    )}
                                                </button>
                                            )}

                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-woodyBrown hover:bg-woodyBrown/5 disabled:opacity-60"
                                                    onClick={
                                                        handleOpenEditModal
                                                    }
                                                    disabled={isSavingEdit}
                                                >
                                                    <span className="relative h-4 w-4">
                                                        <PenIcon />
                                                    </span>
                                                    {t('modules.messages.edit')}
                                                </button>
                                            )}

                                            {canDelete && (
                                                <MessageDeleteAction
                                                    onDelete={onDelete}
                                                    disabled={isDeleting}
                                                    onAfter={() =>
                                                        setIsActionsOpen(false)
                                                    }
                                                />
                                            )}
                                        </div>
                                    )}
                                </ConfimationModalProvider>
                            </div>
                        )}
                    </div>
                </div>

                {isEditModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                        <div className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
                            <h3 className="text-lg font-semibold text-woodyBrown">
                                {t('modules.messages.editTitle')}
                            </h3>

                            <div className="mt-4 flex flex-col gap-3">
                                <div>
                                    <InputLabel
                                        label={t(
                                            'modules.messages.form.subjectLabel',
                                        )}
                                    />
                                    <input
                                        className="text-input w-full"
                                        value={editSubject}
                                        onChange={event =>
                                            setEditSubject(event.target.value)
                                        }
                                    />
                                </div>

                                <div>
                                    <InputLabel
                                        label={t(
                                            'modules.messages.form.bodyLabel',
                                        )}
                                    />
                                    <textarea
                                        className="text-input w-full min-h-[120px]"
                                        value={editBody}
                                        onChange={event =>
                                            setEditBody(event.target.value)
                                        }
                                    />
                                </div>

                                <div>
                                    <InputLabel
                                        label={t(
                                            'modules.messages.form.attachmentsLabel',
                                        )}
                                    />

                                    {existingAttachments.length > 0 && (
                                        <ul className="mb-2 flex flex-col gap-1 text-sm text-mongooseGray">
                                            {existingAttachments.map(
                                                (attachment, index) => (
                                                    <li
                                                        key={`${attachment.id}-${index}`}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <span className="truncate">
                                                            {attachment.name ||
                                                                attachment.id}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="text-venetianRed underline"
                                                            onClick={() =>
                                                                setExistingAttachments(
                                                                    current =>
                                                                        current.filter(
                                                                            (
                                                                                _,
                                                                                currentIndex,
                                                                            ) =>
                                                                                currentIndex !==
                                                                                index,
                                                                        ),
                                                                )
                                                            }
                                                        >
                                                            {t('common.remove')}
                                                        </button>
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    )}

                                    <MessageAttachmentsField
                                        attachments={newAttachments}
                                        onChange={setNewAttachments}
                                        disabled={isSavingEdit}
                                        hideLabel
                                    />
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end gap-2">
                                <button
                                    type="button"
                                    className="secondary-btn-small px-3 py-1.5"
                                    onClick={() => setIsEditModalOpen(false)}
                                    disabled={isSavingEdit}
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="button"
                                    className="primary-btn-small px-3 py-1.5 disabled:opacity-60"
                                    onClick={handleSaveEdit}
                                    disabled={
                                        isSavingEdit || !editSubject.trim()
                                    }
                                >
                                    {t('modules.messages.editSave')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {message.body && (
                    <LinkifiedText
                        as="p"
                        text={message.body}
                        className="mt-3 whitespace-pre-line text-sm leading-relaxed"
                    />
                )}

                <div className="mt-1 w-full min-w-0 flex items-center justify-between gap-2">
                    <div className="text-xs text-mongooseGray flex flex-col gap-1"></div>

                    <div
                        className="relative"
                        onMouseEnter={() => setIsViewersDropdownOpen(true)}
                        onMouseLeave={() => setIsViewersDropdownOpen(false)}
                    >
                        <div
                            className="inline-flex items-center gap-1 text-xs text-mongooseGray"
                            aria-label={t('modules.messages.viewsAriaLabel', {
                                count: viewCount,
                            })}
                        >
                            <span
                                className="relative h-4 w-4"
                                aria-hidden="true"
                            >
                                <EyeIcon />
                            </span>
                            <span>{viewCount}</span>
                        </div>

                        {isViewersDropdownOpen && viewCount > 0 && (
                            <div className="absolute right-0 z-20 mt-1 min-w-[200px] rounded-lg border border-starkWhite bg-white p-2 shadow-lg">
                                <p className="mb-1 text-xs font-semibold text-woodyBrown">
                                    {t('modules.messages.viewsViewedBy')}
                                </p>
                                {isLoadingViewers ? (
                                    <p className="text-xs text-mongooseGray">
                                        {t('common.loading')}
                                    </p>
                                ) : (
                                    <ul className="max-h-44 overflow-y-auto text-xs text-mongooseGray">
                                        {viewerLabels.map(viewer => (
                                            <li
                                                key={viewer.userId}
                                                className="py-0.5"
                                            >
                                                {viewer.label}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {attachments?.length ? (
                <div
                    className={`flex flex-col ${isFromRelative ? '' : 'mt-3'}`}
                >
                    <hr className="border-woodyBrown border-1 opacity-30 -mx-4" />
                    <p className="mt-2 mb-2 text-sm font-semibold">
                        {t('modules.messages.attachmentsHeading')}:
                    </p>
                    <div className="flex min-w-0 flex-wrap gap-3">
                        {attachments.map(attachment => {
                            const label = attachment.name || attachment.id;
                            const type = resolveAttachmentType(
                                attachment.mimeType,
                            );
                            const imageIndex =
                                type === 'image'
                                    ? imageAttachments.indexOf(attachment)
                                    : -1;
                            const thumbSource =
                                type === 'image' || type === 'video'
                                    ? attachment.thumbnailUrl || attachment.url
                                    : undefined;

                            return (
                                <button
                                    key={attachment.id}
                                    className="w-16 h-16 rounded-lg border border-woodyBrown/30 bg-woodyBrown/10 text-xs text-woodyBrown flex items-center justify-center text-center focus:outline-none focus:ring-2 focus:ring-woodyBrown/10 overflow-hidden"
                                    title={label}
                                    type="button"
                                    onClick={() => {
                                        setPreviewAttachment({
                                            attachment,
                                            imageIndex:
                                                imageIndex >= 0 &&
                                                imageAttachments.length > 1
                                                    ? imageIndex
                                                    : undefined,
                                        });
                                    }}
                                >
                                    {thumbSource ? (
                                        <div className="relative w-full h-full">
                                            <div
                                                className="absolute inset-0 bg-center bg-cover"
                                                style={{
                                                    backgroundImage: `url(${thumbSource})`,
                                                }}
                                            />
                                            {type === 'video' ? (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                                    <span className="text-white text-xs">
                                                        ▶
                                                    </span>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <span className="text-center text-woodyBrown break-words line-clamp-2 px-2">
                                            {label}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {(commentsEnabled || comments.length > 0) && (
                <div className={isFromRelative ? '' : 'mt-3'}>
                    <hr className="border-woodyBrown border-1 opacity-30 -mx-4" />
                    <p className="mt-2 text-sm font-semibold">
                        {t('modules.messages.commentsTitle')}:
                    </p>
                    {comments.length ? (
                        <div className="mt-2 flex min-w-0 flex-col gap-2">
                            {hasCollapsibleComments && (
                                <button
                                    type="button"
                                    className="self-start inline-flex items-center gap-1 py-1 text-xs text-woodyBrown hover:underline"
                                    aria-expanded={isCommentsExpanded}
                                    onClick={() =>
                                        setIsCommentsExpanded(
                                            current => !current,
                                        )
                                    }
                                >
                                    <span
                                        className="relative h-3 w-3 shrink-0"
                                        aria-hidden="true"
                                    >
                                        <CaretIcon
                                            className={
                                                isCommentsExpanded
                                                    ? ''
                                                    : 'rotate-180'
                                            }
                                        />
                                    </span>
                                    {isCommentsExpanded
                                        ? t(
                                              'modules.messages.commentsShowLatest',
                                              {
                                                  count: COMMENT_PREVIEW_LIMIT,
                                              },
                                          )
                                        : t(
                                              'modules.messages.commentsShowOlder',
                                              {
                                                  count:
                                                      commentsTotal -
                                                      visibleComments.length,
                                              },
                                          )}
                                </button>
                            )}
                            {visibleComments.map((comment: MessageComment) => (
                                <ConfimationModalProvider
                                    key={comment.id}
                                    title={t(
                                        'modules.messages.commentsDeleteConfirmTitle',
                                    )}
                                    message={t(
                                        'modules.messages.commentsDeleteConfirmMessage',
                                    )}
                                >
                                    <MessageCommentItem
                                        comment={comment}
                                        viewers={viewerLabels.filter(
                                            viewer =>
                                                viewer.userId !==
                                                    comment.authorId &&
                                                Date.parse(viewer.readAt) >=
                                                    Date.parse(
                                                        comment.createdDate,
                                                    ),
                                        )}
                                        viewersLoaded={
                                            viewCount === 0 ||
                                            viewersResponse !== undefined
                                        }
                                        isLoadingViewers={isLoadingViewers}
                                        onRequestViewers={() =>
                                            setHasRequestedCommentViewers(true)
                                        }
                                        canManage={Boolean(
                                            currentUserId &&
                                                currentUserId ===
                                                    comment.authorId,
                                        )}
                                        isBusy={
                                            editingCommentId === comment.id ||
                                            deletingCommentId === comment.id
                                        }
                                        onUpdate={body =>
                                            handleUpdateComment(
                                                comment.id,
                                                body,
                                            )
                                        }
                                        onDelete={() =>
                                            handleDeleteComment(comment.id)
                                        }
                                    />
                                </ConfimationModalProvider>
                            ))}
                        </div>
                    ) : (
                        <p className="mt-2 text-xs text-mongooseGray">
                            {isValidating
                                ? t('common.loading')
                                : t('modules.messages.commentsEmpty')}
                        </p>
                    )}

                    {!commentsEnabled && (
                        <p className="mt-2 text-xs text-mongooseGray">
                            {t('modules.messages.commentsClosed')}
                        </p>
                    )}

                    {commentsEnabled && canComment && (
                        <>
                            {!isCommentComposerOpen ? (
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full items-center gap-2 rounded-lg border border-woodyBrown/20 bg-white/70 px-3 py-2 text-left text-sm font-medium text-woodyBrown hover:bg-woodyBrown/5"
                                    onClick={() =>
                                        setIsCommentComposerOpen(true)
                                    }
                                >
                                    <span className="relative h-4 w-4">
                                        <MessageBubbleIcon />
                                    </span>
                                    {t('modules.messages.commentsLabel')}
                                </button>
                            ) : (
                                <div className="mt-3 flex flex-col">
                                    <InputLabel
                                        label={t(
                                            'modules.messages.commentsLabel',
                                        )}
                                    />
                                    <textarea
                                        className="text-input w-full min-h-[80px]"
                                        value={commentBody}
                                        onChange={event =>
                                            setCommentBody(event.target.value)
                                        }
                                        autoFocus
                                    />
                                    <div className="mt-2 flex justify-end gap-2">
                                        <button
                                            type="button"
                                            className="secondary-btn-small py-1.5 px-3"
                                            onClick={() => {
                                                setCommentBody('');
                                                setIsCommentComposerOpen(false);
                                            }}
                                            disabled={isSubmittingComment}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            className="primary-btn-small py-1.5 px-3 disabled:opacity-60"
                                            onClick={handleSubmitComment}
                                            disabled={
                                                !commentBody.trim().length ||
                                                isSubmittingComment
                                            }
                                        >
                                            {t(
                                                'modules.messages.commentsSubmit',
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </article>
    );
};

const MessageDeleteAction = ({
    onDelete,
    disabled,
    onAfter,
}: {
    onDelete: () => void;
    disabled: boolean;
    onAfter: () => void;
}) => {
    const { t } = useTranslation();
    const { showConfirmation } = useConfirmationModalContext();

    return (
        <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:opacity-60"
            disabled={disabled}
            onClick={async () => {
                const confirmed = await showConfirmation();
                if (confirmed) {
                    onDelete();
                }
                onAfter();
            }}
        >
            <span className="relative h-4 w-4">
                <TrashIcon />
            </span>
            {t('common.delete')}
        </button>
    );
};
